"use client";

import { useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Clock, PhoneOff, CalendarCheck, Users, Star } from "lucide-react";
import Link from "next/link";
import { CTA } from "~/components/inbound-gpt/CTA";
import { FAQ } from "~/components/inbound-gpt/FAQ";
import { HowItWorks } from "~/components/inbound-gpt/HowItWorks";
import { Platform } from "~/components/inbound-gpt/Platform";
import { SiteGlueFaqPage } from "~/components/inbound-gpt/SiteGlueFaqPage";
import { SiteGluePlatformPage } from "~/components/inbound-gpt/SiteGluePlatformPage";
import { UseCases } from "~/components/inbound-gpt/UseCases";
import { scrollToInboundSection } from "~/lib/inbound-gpt/paths";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";

// export const metadata = {
//   title: "Case Studies — Healthcare AI Results | Inbound GPT",
//   description: "See how dental, PT, chiropractic, and other healthcare practices reduced missed calls and increased bookings with Inbound GPT AI.",
// };

const caseStudies = [
  {
    specialty: "Dental",
    practice: "Aesthetic Smile Design",
    description: "A multi-location dental practice reduced missed calls by 87% and increased new patient bookings by 34% after deploying Inbound GPT.",
    stats: [
      { label: "Missed Calls Reduced", value: "87%", icon: PhoneOff },
      { label: "New Patient Bookings", value: "+34%", icon: CalendarCheck },
      { label: "Staff Hours Saved/Week", value: "25+", icon: Clock },
    ],
  },
  {
    specialty: "Physical Therapy",
    practice: "Peak Performance PT",
    description: "Inbound GPT handles after-hours calls, appointment scheduling, and patient follow-ups, freeing up front desk staff to focus on in-office patients.",
    stats: [
      { label: "After-Hours Calls Handled", value: "92%", icon: PhoneOff },
      { label: "Patient Satisfaction", value: "4.9/5", icon: Star },
      { label: "Appointments Booked/Month", value: "340+", icon: CalendarCheck },
    ],
  },
  {
    specialty: "Chiropractic",
    practice: "Aligned Wellness Center",
    description: "Automated patient intake and appointment reminders led to a 45% reduction in no-shows and a significant improvement in patient retention.",
    stats: [
      { label: "No-Show Reduction", value: "45%", icon: TrendingUp },
      { label: "Patient Retention", value: "+28%", icon: Users },
      { label: "Daily Calls Automated", value: "60+", icon: PhoneOff },
    ],
  },
  {
    specialty: "Optometry",
    practice: "ClearView Eye Care",
    description: "Inbound GPT manages appointment scheduling, insurance verification inquiries, and recall reminders across three locations.",
    stats: [
      { label: "Scheduling Efficiency", value: "+52%", icon: CalendarCheck },
      { label: "Patient Inquiries Handled", value: "200+/wk", icon: Users },
      { label: "Staff Overtime Reduced", value: "90%", icon: Clock },
    ],
  },
  {
    specialty: "Cosmetic Surgery",
    practice: "Premier Aesthetics",
    description: "Lead qualification and consultation booking improved dramatically, with Inbound GPT screening and scheduling prospective patients 24/7.",
    stats: [
      { label: "Lead Qualification Rate", value: "+67%", icon: TrendingUp },
      { label: "Consultation Bookings", value: "+41%", icon: CalendarCheck },
      { label: "Response Time", value: "<30s", icon: Clock },
    ],
  },
  {
    specialty: "Dermatology",
    practice: "SkinFirst Dermatology",
    description: "Inbound GPT handles patient triage, appointment scheduling, and prescription refill requests, reducing front desk workload significantly.",
    stats: [
      { label: "Front Desk Workload", value: "-55%", icon: TrendingUp },
      { label: "Patient Wait Time", value: "-40%", icon: Clock },
      { label: "Satisfaction Score", value: "4.8/5", icon: Star },
    ],
  },
  {
    specialty: "Internal Medicine",
    practice: "Wellness Internal Medicine",
    description: "After-hours call handling and patient screening helped this practice manage high call volumes while maintaining quality patient communication.",
    stats: [
      { label: "Calls Handled After Hours", value: "95%", icon: PhoneOff },
      { label: "Patient Callbacks Eliminated", value: "78%", icon: TrendingUp },
      { label: "Monthly Time Saved", value: "100+ hrs", icon: Clock },
    ],
  },
  {
    specialty: "Sports Medicine",
    practice: "Elite Sports Medicine",
    description: "Inbound GPT streamlined injury consultation scheduling and follow-up coordination, improving both patient outcomes and operational efficiency.",
    stats: [
      { label: "Scheduling Speed", value: "+60%", icon: CalendarCheck },
      { label: "Follow-Up Compliance", value: "+35%", icon: Users },
      { label: "Patient Volume Growth", value: "+22%", icon: TrendingUp },
    ],
  },
  {
    specialty: "Orthodontics",
    practice: "BrightSmile Orthodontics",
    description: "Automated appointment reminders, treatment plan inquiries, and new patient onboarding reduced administrative overhead across two locations.",
    stats: [
      { label: "Admin Overhead Reduced", value: "48%", icon: TrendingUp },
      { label: "New Patient Onboarding", value: "+38%", icon: Users },
      { label: "Appointment Reminders Sent", value: "500+/mo", icon: CalendarCheck },
    ],
  },
];

export function CaseStudiesPageContent() {
  return (
    <div className="pb-16 pt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl font-bold tracking-tight"
            >
              Results by <span className="text-primary">Specialty</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto"
            >
              Specialty outcomes from Inbound GPT deployments. For cloneable agents, browse{" "}
              <Link href="/agents" className="text-primary hover:underline">
                Agent Templates
              </Link>
              ; for narrative proof points, see{" "}
              <Link href="/customer-stories" className="text-primary hover:underline">
                Customer Stories
              </Link>
              .
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.specialty}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="rounded-md border border-border bg-card p-6 flex flex-col hover-elevate"
                data-testid={`card-case-study-${study.specialty.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider">{study.specialty}</span>
                </div>
                <h3 className="text-lg font-bold tracking-tight mb-2">{study.practice}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">{study.description}</p>
                <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border">
                  {study.stats.map((stat) => (
                    <div key={stat.label} className="text-center">
                      <stat.icon className="w-4 h-4 text-primary mx-auto mb-1" />
                      <p className="text-lg font-bold tracking-tight">{stat.value}</p>
                      <p className="text-[10px] text-muted-foreground leading-tight">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-muted-foreground text-sm">
              Learn{" "}
              <Link href="/how-it-works" className="text-primary hover:underline">How It Works</Link>,{" "}
              read our{" "}
              <Link href="/faq" className="text-primary hover:underline">FAQ</Link>,{" "}
              or{" "}
              <Link href="/how-it-works" className="text-primary hover:underline">see a live demo</Link>.
            </p>
          </div>
        </div>
    </div>
  );
}

export function HowItWorksPageContent() {
  return (
    <div className="pb-16">
        <HowItWorks />
        <CTA />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 text-center">
          <p className="text-muted-foreground text-sm">
            See real results in our{" "}
            <Link href="/case-studies" className="text-primary hover:underline">Case Studies</Link>{" "}
            or explore the{" "}
            <Link href="/platform" className="text-primary hover:underline">Platform</Link>.{" "}
            Have questions? Visit our{" "}
            <Link href="/faq" className="text-primary hover:underline">FAQ</Link>.
          </p>
        </div>
      </div>
  );
}

export function PlatformPageContent() {
  const isSiteGlue = getMarketingChrome().brandId === "siteglue";

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (hash === "pricing") {
      window.location.replace("/pricing");
      return;
    }
    if (!hash) return;
    const timer = window.setTimeout(() => scrollToInboundSection(hash), 100);
    return () => window.clearTimeout(timer);
  }, []);

  if (isSiteGlue) {
    return <SiteGluePlatformPage />;
  }

  return (
    <div className="pb-16">
      <Platform />
      <UseCases />
      <CTA />
    </div>
  );
}

export function FaqPageContent() {
  const isSiteGlue = getMarketingChrome().brandId === "siteglue";

  if (isSiteGlue) {
    return <SiteGlueFaqPage />;
  }

  return (
    <div className="pb-16 pt-8">
      <div className="mx-auto mb-8 max-w-3xl px-4 sm:px-6 lg:px-8">
        <nav
          className="flex items-center gap-2 text-sm text-muted-foreground"
          data-testid="faq-breadcrumb"
        >
          <Link href="/" className="transition-colors hover:text-foreground">
            Home
          </Link>
          <span>/</span>
          <span className="text-foreground">FAQ</span>
        </nav>
      </div>
      <FAQ />
      <div className="mx-auto mt-12 max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <p className="text-sm text-muted-foreground" data-testid="text-faq-cta">
          Still have questions?{" "}
          <Link
            href="/about"
            className="text-primary hover:underline"
            data-testid="link-faq-contact"
          >
            Contact us
          </Link>{" "}
          or explore our{" "}
          <Link
            href="/help-center"
            className="text-primary hover:underline"
            data-testid="link-faq-help"
          >
            Help Center
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

